import type { PaginatedResponse } from "../../types/pagination";
import { MetadataIcon } from "../common/types";
import { HarnessId } from "../harness-capabilities/types";

export interface FlowSessionMetadata {
  name: string;
  summary: string;
  color: string;
}

export interface FollowUpMessage {
  title: string;
  icon: MetadataIcon;
  content: string;
}

export type FlowSessionClass = "canvas" | "workspace" | "action_item";

/** Canonical list of flow-session modes — the single source of truth for the
 * values. The backend resolves the model, tools, and instructions for a
 * session from its mode via flowconfig. */
export const FLOW_SESSION_MODES = {
  WORKSPACE_CHAT: "workspace_chat",
  CANVAS_CHAT: "canvas_chat",
  CANVAS_ONBOARDING: "canvas_onboarding",
  CANVAS_SUBAGENT_ASYNC: "canvas_subagent_async",
  CANVAS_SUBAGENT_SYNC: "canvas_subagent_sync",
} as const;

export type FlowSessionMode =
  (typeof FLOW_SESSION_MODES)[keyof typeof FLOW_SESSION_MODES];

/** How much a turn asks before running tools: confirm-mutations parks every
 * mutating call, auto defers to the harness's own permission model (the
 * default), allow-all asks nothing. Per-message and sticky — a message may
 * switch it, and the switch is stamped back onto the session. */
export const PERMISSION_MODES = {
  CONFIRM_MUTATIONS: "confirm-mutations",
  AUTO: "auto",
  ALLOW_ALL: "allow-all",
} as const;

export type PermissionMode =
  (typeof PERMISSION_MODES)[keyof typeof PERMISSION_MODES];

/** Work-item phases mirrored from agentd: `running` until a terminal phase. */
export type FlowSessionWorkItemPhase =
  | "running"
  | "completed"
  | "failed"
  | "stopped"
  | "killed_at_ceiling"
  | "lost"
  | "reaped";

/**
 * One background work item a harness turn spawned — a subagent, a background
 * command, or a process — mirrored from agentd's lifecycle events. `call_id`
 * is the tool call (Agent/Task/Bash) that spawned it, which is how the chat
 * joins it to the rail; `turn_id` is the platform turn that spawned it and
 * `follow_up_turn_id` the turn that delivered its result.
 */
export interface FlowSessionWorkItem {
  work_item_id: string;
  kind: "subagent" | "command" | "process";
  call_id?: string;
  parent_work_item_id?: string;
  turn_id: string;
  name?: string;
  subagent_type?: string;
  description?: string;
  phase: FlowSessionWorkItemPhase;
  summary?: string;
  last_tool?: string;
  tool_uses?: number;
  total_tokens?: number;
  duration_ms?: number;
  error?: string;
  follow_up_turn_id?: string;
  started_at: string;
  updated_at: string;
  ended_at?: string;
  ceiling_at?: string;
}

export interface FlowSession {
  id: string;
  user_id: string;
  organization_id: string;
  class: FlowSessionClass;
  mode: FlowSessionMode;
  /** Runtime the whole conversation runs on, stamped at create and re-stampable via update
   *  only until the first message. Absent on sessions that predate the stamp, which ran the
   *  legacy loop — read it as `cohesive`. */
  harness?: HarnessId;
  /** Model the harness turns run, switched by sending a message with a new one. Absent means
   *  the harness default; always absent on cohesive, whose dial is the reasoning effort tier. */
  model?: string;
  /** Harness effort the user last picked, switched by sending a message with a new one. Absent
   *  means the default; may name one the current model doesn't offer after a model switch. */
  reasoning_effort?: string;
  /** Background work the session's harness turns spawned (subagents, commands). Only on the REST
   *  session; the `flow_session.updated` event carries just `active_work_items`. */
  work_items?: FlowSessionWorkItem[];
  /** Count of work items still running, from the `flow_session.updated` event. */
  active_work_items?: number;
  name: string;
  status:
    | "deleted"
    | "enabled"
    | "ready"
    | "initialized"
    | "processing"
    | "closed";
  metadata: FlowSessionMetadata;
  follow_up_data?: {
    flow_message_id: string;
    flow_message_sent_at: string;
    messages: FollowUpMessage[];
  };
  read_status: "unread" | "read";
  canvas_id?: string;
  action_item_id?: string;
  workspace_id?: string;
  /** Set when the chat was trashed with its canvas/workspace; restore that parent to bring it back. */
  archived_with_resource_id?: string;
  invoked_platforms?: FlowSessionInvokedPlatform[];
  permission_mode: PermissionMode;
  /** Set while the chat is archived; null/absent means active. */
  archived_at?: string;
  /** Opens the chat in the app for a signed-in user. */
  dashboard_url?: string;
  created_at: string;
  updated_at: string;

  // custom frontend only props
  typewrite?: boolean;
}

export interface FlowSessionInvokedPlatform {
  name: string;
  display_name?: string;
  image_url?: string;
}

export interface FlowSessionsResponse {
  data: {
    flow_sessions: FlowSession[];
  };
}

/** Archived-list rows carry parent state so the client can disable Restore preemptively. */
export type ArchivedFlowSession = FlowSession & {
  /** True when the parent canvas/workspace is also archived — restore that parent first. */
  parent_archived: boolean;
};

export type ArchivedFlowSessionListResponse = PaginatedResponse<{
  flow_sessions: ArchivedFlowSession[];
}>;

export interface FlowSessionResponse {
  data: {
    flow_session: FlowSession;
  };
}

export interface CreateFlowSessionRequest {
  mode: FlowSessionMode;
  class: FlowSessionClass;
  project_id?: string;
  canvas_id?: string;
  workspace_id?: string;
  /** Omitted is cohesive. Re-stampable via update only until the first message. */
  harness?: HarnessId;
  /** Optional even on a harness: omitted resolves that harness's default per turn. Rejected
   *  on cohesive, which takes no model. */
  model?: string;
}

export interface UpdateFlowSessionReadStatusPayload {
  read_status: string;
}

export interface SetFlowSessionNameRequest {
  name: string;
}

export interface UpdateFlowSessionRequest {
  name?: string;
  permission_mode?: PermissionMode;
  /** Re-stamp what the chat runs on — accepted only while it has no messages (422 after).
   *  The pair travels together: a model alone re-pins on the current harness; an omitted
   *  model keeps the pin, except across a harness change, which resets it to the new
   *  harness's default. */
  harness?: HarnessId;
  model?: string;
}
